/**
 * The chrome the enrol, *Add photo* and encounter sheets share (slices 04–07): the form around a
 * header, a scrolling body and a footer; the header with its close; what still blocks the tap;
 * the footer while the operator edits; and the footer's buttons after the tap. Pure.
 */
import { type LucideIcon, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'
import type { EncounterGap } from '../encounter/encounter-draft'
import { MODAL_FOOTER, MODAL_ICON_TARGET, MODAL_PRIMARY, TEXT_CAPTION } from '../recipes'
import type { EnrolFooterFlow } from './EnrolPanelStatus'

/** The header's close is hidden while a request runs and while the photo waits for a retry. */
export function isClosable(flow: EnrolFooterFlow | undefined): boolean {
  if (!flow) return true
  const { step } = flow.save
  if (step === 'enrolling' || step === 'uploading') return false
  return !(step === 'failed' && flow.subjectCreated)
}

interface SheetFormProps {
  /** Id of the panel title, for `aria-labelledby`. */
  titleId: string
  onSubmit: () => void
  slots: { header: ReactNode; footer: ReactNode }
  children: ReactNode
}

/** The sheet's form: the header, the body scrolling between it and the footer. */
export function SheetForm({ titleId, onSubmit, slots, children }: SheetFormProps) {
  return (
    <form
      aria-labelledby={titleId}
      className="@container/modal flex min-h-0 flex-1 flex-col"
      onSubmit={event => {
        event.preventDefault()
        onSubmit()
      }}
    >
      {slots.header}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-1 pb-5 @3xl/modal:px-6">
        {children}
      </div>
      {slots.footer}
    </form>
  )
}

interface SheetHeaderProps {
  /** Show the close button (see `isClosable`). */
  closable: boolean
  onClose: () => void
  children: ReactNode
}

/** The sheet's header, with the close button at its end while closing is safe. */
export function SheetHeader({ closable, onClose, children }: SheetHeaderProps) {
  const { t } = useTranslation()
  return (
    <header className="flex shrink-0 items-start gap-3 px-4 pt-2 pb-3 @3xl/modal:px-6 @3xl/modal:pt-5 @3xl/modal:pb-4">
      {children}
      {closable && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={MODAL_ICON_TARGET}
          onClick={onClose}
          aria-label={t('humint.common.actions.close')}
        >
          <X className="size-6 @3xl/modal:size-5" />
        </Button>
      )}
    </header>
  )
}

/** What still blocks the tap, in words beside the button. */
function GapsHint({ gaps }: { gaps: readonly EncounterGap[] }) {
  const { t } = useTranslation()
  if (gaps.length === 0) return null
  return (
    <p role="status" className={`${TEXT_CAPTION} font-medium text-destructive @3xl/modal:mr-auto`}>
      {t(`humint.check.enrol.gaps.${gaps[0]}`)}
    </p>
  )
}

interface SheetEditFooterProps {
  /** What still blocks the tap: named in place of the note, and the submit disabled. */
  gaps: readonly EncounterGap[]
  /** Shown beside the buttons while nothing blocks the tap. */
  note?: ReactNode
  submit: { Icon: LucideIcon; label: string }
  onClose: () => void
}

/** The footer while editing: the note or what blocks the tap, cancel (dialog only), submit. */
export function SheetEditFooter({ gaps, note, submit, onClose }: SheetEditFooterProps) {
  const { t } = useTranslation()
  const blocked = gaps.length > 0
  return (
    <footer className={MODAL_FOOTER}>
      {blocked ? <GapsHint gaps={gaps} /> : note}
      <Button
        type="button"
        variant="ghost"
        size="default"
        // Without a note to push them, the buttons keep to the end of the row themselves.
        className={cn('hidden rounded-xl', !note && '@3xl/modal:ml-auto', '@3xl/modal:inline-flex')}
        onClick={onClose}
      >
        {t('humint.common.actions.cancel')}
      </Button>
      <Button type="submit" variant="solid" size="2xl" className={MODAL_PRIMARY} disabled={blocked}>
        <submit.Icon aria-hidden className="size-5 @3xl/modal:size-4" />
        {submit.label}
      </Button>
    </footer>
  )
}

/** The icon in the sheet's primary button. */
export const SHEET_PRIMARY_ICON = 'size-5 @3xl/modal:size-4'

interface SheetPrimaryButtonProps {
  children: ReactNode
  onClick?: () => void
  /** Send the form again (a retry); otherwise a plain tap. */
  submit?: boolean
  loading?: boolean
}

/** The sheet's one copper button: "submit" sends the form again, anything else is a plain tap. */
export function SheetPrimaryButton({
  children,
  onClick,
  submit = false,
  loading = false,
}: SheetPrimaryButtonProps) {
  return (
    <Button
      type={submit ? 'submit' : 'button'}
      variant="solid"
      size="2xl"
      className={MODAL_PRIMARY}
      onClick={onClick}
      loading={loading}
    >
      {children}
    </Button>
  )
}

/** A safe way out beside the primary button: "close", "close without the photo", "done". */
export function SheetSecondaryButton({
  children,
  onClick,
}: {
  children: ReactNode
  onClick: () => void
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="xl"
      className="w-full rounded-xl text-base font-semibold @3xl/modal:h-10 @3xl/modal:w-auto @3xl/modal:px-4 @3xl/modal:text-sm"
      onClick={onClick}
    >
      {children}
    </Button>
  )
}
